import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import type { SelectOption } from '@ps/web-shared-components';
import { required } from '@ps/web-shared-validators';
import classNames from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import { Country as CountryEnum } from '@swf/core/enums';
import { select } from '@swf/core/store';

import Alert from '../../../../../../components/Alert/Alert';
import { getCountryStates } from '../../../../../../utils/country-states/country-states';
import { getFrenchCitiesByState } from '../../../../../../utils/placeOfBirth';
import Address from '../../../../../Address/Address';
import City from '../../../../../City/City';
import Country from '../../../../../Country/Country';
import { findCountry } from '../../../../../CountrySelect/CountrySelect';
import InfoPrompt from '../../../../../InfoPrompt/InfoPrompt';
import PostalCode from '../../../../../PostalCode/PostalCode';
import { findState } from '../../../../../State/State';
import RMFDropDown from '../../../RMFDropDown/RMFDropDown';

import {
  TXTCLI_City,
  TXTCLI_Country_canXt_be_changed,
  TXTCLI_You_must_be_a_resident_of_the__ELL,
  TXTWCL_Country_cannot_be_changed,
  TXTWCL_Just_so_you_know,
  TXTWCL_Please_select_City,
  TXTWCL_Select_city_1,
  TXTWCL_You_canXt_use_a_PO_Box_as_your_ELL,
  TXTWCL_You_must_be_a_resident_of_N0_t_ELL,
  TXTWCL_You_must_be_a_resident_of_Onta_ELL
} from './Fields.messages.json';
import {
  alert,
  alertTitle,
  info,
  infoText,
  inputText,
  manual,
  manualDropdown,
  manualHidden,
  manualVisible
} from './Fields.scss';
import State from './components/StateField';

interface Props {
  formVisible: boolean;
  addressSearchFeature: boolean;
  setCountry: (arg: string) => void;
}

const Fields: React.FC<Props> = ({ addressSearchFeature, formVisible, setCountry }) => {
  const [cityOptions, setCityOptions] = useState<SelectOption[]>([]);
  const signupCountry = useSelector(select.account.getCountry);
  const isAutofocusEnabled = useFeature(FeatureFlag.SwcSignupRmfAutofocus);
  const isSiteUS = useSelector(select.cms.isSiteUS);

  const {
    input: { value: country }
  } = useField('country', { initialValue: signupCountry });
  const siteId = useSelector(select.cms.getSiteId);
  const isCAON = siteId === Sites.CA_ON;

  const {
    input: { value: state }
  } = useField('state', { initialValue: isCAON ? 'ON' : '' });
  const {
    input: { value: selectedCity }
  } = useField('city');

  const isFR = siteId === Sites.FRA && country === CountryEnum.France;
  const showPrompt =
    [Sites.CA_ON, Sites.ROM, Sites.POR, Sites.DE, Sites.GR].includes(siteId) || isFR || isSiteUS;
  const CITY_MAX_LENGTH_SHORT = 20;
  const CITY_MAX_LENGTH_LONG = 60;

  const setFrenchCities = useCallback(async () => {
    const frenchCitiesByState = await getFrenchCitiesByState(
      findState(state, getCountryStates(country))?.value ?? ''
    );
    setCityOptions(frenchCitiesByState);
  }, [country, state]);

  useEffect(() => {
    if (isFR) setFrenchCities();
  }, [isFR, setFrenchCities]);

  useEffect(() => {
    setCountry(country);
  }, [country, setCountry]);

  return (
    <fieldset
      className={classNames(
        manual,
        formVisible || !addressSearchFeature ? manualVisible : manualHidden,
        { [manualDropdown]: isFR }
      )}
      data-testid="address-form"
    >
      {isCAON && (
        <Alert variant="info" className={alert}>
          <div className={alertTitle}>
            <Translation {...TXTWCL_Just_so_you_know} />
          </div>
          <Translation {...TXTWCL_You_canXt_use_a_PO_Box_as_your_ELL} />
        </Alert>
      )}
      <Country
        country={country}
        fieldName="country"
        isRmfFlow
        labelClassName={classNames({ [info]: showPrompt })}
        labelContent={
          showPrompt ? (
            <InfoPrompt
              title={{
                translation:
                  isSiteUS || siteId === Sites.POR || siteId === Sites.GR
                    ? TXTCLI_Country_canXt_be_changed
                    : TXTWCL_Country_cannot_be_changed
              }}
            >
              <p className={infoText}>
                {isSiteUS ? (
                  <Translation {...TXTCLI_You_must_be_a_resident_of_the__ELL} />
                ) : isCAON ? (
                  <Translation {...TXTWCL_You_must_be_a_resident_of_Onta_ELL} />
                ) : (
                  <Translation
                    values={{ 0: findCountry(country).text }}
                    {...TXTWCL_You_must_be_a_resident_of_N0_t_ELL}
                  />
                )}
              </p>
            </InfoPrompt>
          ) : null
        }
        className={classNames({ [inputText]: showPrompt })}
      />
      <section>
        <Address
          country={country}
          fieldName="streetAddress"
          autofocus={(formVisible || !addressSearchFeature) && isAutofocusEnabled}
        />
        {isFR ? (
          <>
            <State country={country} fieldName="state" />
            <RMFDropDown
              id="city"
              label={TXTCLI_City}
              placeholder={selectedCity ? selectedCity : <Translation {...TXTWCL_Select_city_1} />}
              options={cityOptions}
              validators={[required(TXTWCL_Please_select_City)]}
              disabled={!cityOptions?.length}
            />
          </>
        ) : (
          <>
            <City
              maxLength={siteId === Sites.DEN ? CITY_MAX_LENGTH_SHORT : CITY_MAX_LENGTH_LONG}
              fieldName="city"
            />
            <State country={country} fieldName="state" />
          </>
        )}
        <PostalCode country={country} fieldName="zip" />
      </section>
    </fieldset>
  );
};

export default Fields;
